<template>
  <b-row>
    <b-col xl="12">
      <transition name="slide">
        <b-card>
          <div slot="header">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <b-button
                  v-if="profile.role === 'root'"
                  variant="secondary"
                  size="sm"
                  class="mr-3"
                  @click="goBack"
                >
                  <i class="fas fa-arrow-left"></i> Voltar
                </b-button>
                <strong>Gestão de Relacionamentos</strong>
              </div>
              <div>
                <b-button
                  v-if="canManageRelationships"
                  variant="primary"
                  class="mr-2"
                  @click="openCreateForm('internal')"
                >
                  <i class="fas fa-plus-circle"></i> Adicionar Interno
                </b-button>
                <b-button
                  v-if="canManageRelationships"
                  variant="info"
                  @click="openCreateForm('third')"
                >
                  <i class="fas fa-plus-circle"></i> Adicionar Terceiro
                </b-button>
                <b-button
                  v-if="canManageRelationships"
                  variant="warning"
                  class="ml-2"
                  @click="openImportModal"
                >
                  <i class="fas fa-file-import"></i> Importar CSV
                </b-button>
              </div>
            </div>
          </div>

          <!-- Tabs para alternar entre colaboradores internos e terceiros -->
          <b-tabs content-class="mt-3" fill>
            <b-tab title="Funcionários" active>
              <div class="table-container">
                <b-table
                  :items="internalEmployees"
                  :fields="internalFields"
                  :busy="loadingInternal"
                  thead-class="employees-header"
                  :tbody-tr-class="rowClass"
                  borderless
                  hover
                  responsive
                  show-empty
                  empty-text="Nenhum funcionário encontrado"
                  @row-clicked="openEditForm"
                >
                  <template #cell(name)="data">
                    <div class="employee-name">
                      <strong>{{ data.item.name }}</strong>
                    </div>
                  </template>

                  <template #cell(email)="data">
                    <div class="employee-email">
                      <i class="fas fa-envelope mr-1"></i>
                      {{ data.item.email }}
                    </div>
                  </template>

                  <template #cell(phone)="data">
                    <div class="employee-phone">
                      <i class="fas fa-phone mr-1"></i>
                      {{ data.item.phone || "Não informado" }}
                    </div>
                  </template>

                  <template #cell(area)="data">
                    <div class="employee-area">
                      <i class="fas fa-building mr-1"></i>
                      {{ data.item.area || "Não informado" }}
                    </div>
                  </template>

                  <template #cell(position)="data">
                    <div class="employee-position">
                      <i class="fas fa-briefcase mr-1"></i>
                      {{ data.item.position || "Não informado" }}
                    </div>
                  </template>

                  <template #cell(cpfcnpj)="data">
                    <div class="employee-cpfcnpj">
                      <i class="fas fa-id-card mr-1"></i>
                      {{ data.item.cpfcnpj || "Não informado" }}
                    </div>
                  </template>

                  <template #cell(active)="data">
                    <div class="employee-status">
                      <b-badge
                        :variant="data.item.active ? 'success' : 'secondary'"
                      >
                        {{ data.item.active ? "Ativo" : "Inativo" }}
                      </b-badge>
                    </div>
                  </template>

                  <template #table-busy>
                    <div class="text-center py-5">
                      <b-spinner></b-spinner>
                      <span class="ml-2">Carregando...</span>
                    </div>
                  </template>

                  <template #empty>
                    <div class="text-muted text-center py-5">
                      <i class="fas fa-users fa-2x"></i>
                      <p class="mt-3">Nenhum funcionário encontrado</p>
                      <p class="text-sm">
                        Clique em "Adicionar Funcionário" para adicionar o
                        primeiro funcionário
                      </p>
                    </div>
                  </template>
                </b-table>
              </div>
            </b-tab>

            <b-tab title="Terceiros">
              <div class="table-container">
                <b-table
                  :items="thirdParties"
                  :fields="thirdFields"
                  :busy="loadingThird"
                  thead-class="employees-header"
                  :tbody-tr-class="rowClass"
                  borderless
                  hover
                  responsive
                  show-empty
                  empty-text="Nenhum terceiro encontrado"
                  @row-clicked="openEditForm"
                >
                  <template #cell(type)="data">
                    <div class="third-party-type">
                      <b-badge
                        :variant="
                          data.item.type === 'individuo' ? 'primary' : 'info'
                        "
                      >
                        {{
                          data.item.type === "individuo"
                            ? "Indivíduo"
                            : "Entidade"
                        }}
                      </b-badge>
                    </div>
                  </template>

                  <template #cell(name)="data">
                    <div class="third-party-name">
                      <strong>
                        {{ data.item.name }}
                      </strong>
                    </div>
                  </template>

                  <template #cell(email)="data">
                    <div class="third-party-email">
                      <i class="fas fa-envelope mr-1"></i>
                      {{
                        data.item.type === "individuo"
                          ? data.item.personEmail
                          : data.item.entityEmail
                      }}
                    </div>
                  </template>

                  <template #cell(phone)="data">
                    <div class="third-party-phone">
                      <i class="fas fa-phone mr-1"></i>
                      {{
                        data.item.type === "individuo"
                          ? data.item.personPhone
                          : data.item.entityPhone
                      }}
                    </div>
                  </template>

                  <template #cell(additional)="data">
                    <div class="third-party-additional">
                      <small class="text-muted">
                        {{
                          data.item.type === "individuo"
                            ? `CPF/CNPJ: ${
                                data.item.cpfcnpj || "Não informado"
                              }`
                            : `CNPJ: ${data.item.cpfcnpj || "Não informado"}`
                        }}
                      </small>
                    </div>
                  </template>

                  <template #cell(responsible)="data">
                    <div class="third-party-responsible">
                      <small v-if="data.item.type === 'entidade'">
                        <i class="fas fa-user-tie mr-1"></i>
                        {{ data.item.responsibleName || "Não informado" }}
                      </small>
                      <span v-else class="text-muted">-</span>
                    </div>
                  </template>

                  <template #cell(active)="data">
                    <div class="third-party-status">
                      <b-badge
                        :variant="data.item.active ? 'success' : 'secondary'"
                      >
                        {{ data.item.active ? "Ativo" : "Inativo" }}
                      </b-badge>
                    </div>
                  </template>

                  <template #table-busy>
                    <div class="text-center py-5">
                      <b-spinner></b-spinner>
                      <span class="ml-2">Carregando...</span>
                    </div>
                  </template>

                  <template #empty>
                    <div class="text-muted text-center py-5">
                      <i class="fas fa-handshake fa-2x"></i>
                      <p class="mt-3">Nenhum terceiro encontrado</p>
                      <p class="text-sm">
                        Clique em "Adicionar Terceiro" para adicionar o primeiro
                        terceiro
                      </p>
                    </div>
                  </template>
                </b-table>
              </div>
            </b-tab>
          </b-tabs>
        </b-card>
      </transition>
    </b-col>

    <!-- Sidebar de Importação CSV -->
    <b-sidebar
      id="import-csv-sidebar"
      v-model="isImportVisible"
      title="Importar Relacionamentos via CSV"
      right
      width="50%"
      shadow
      backdrop
      @hidden="resetImport"
    >
      <template #title>
        <div class="d-flex align-items-center w-100">
          <i class="fas fa-file-import mr-2"></i>
          <span>Importar Relacionamentos via CSV</span>
        </div>
      </template>

      <div class="import-sidebar p-3">
        <b-form @submit.prevent="handleImportSubmit">
          <b-form-group
            label="Tipo de relacionamento a importar"
            description="Selecione o tipo do conteúdo do CSV."
          >
            <b-form-radio-group v-model="importType" name="importType" switches>
              <b-form-radio value="internal"
                >Funcionários internos</b-form-radio
              >
              <b-form-radio value="third">Terceiros</b-form-radio>
            </b-form-radio-group>
          </b-form-group>

          <b-alert variant="info" show class="mb-3">
            <div v-if="importType === 'internal'">
              <strong>Formato esperado (Funcionários internos):</strong>
              <ul class="mb-2">
                <li><strong>nome</strong> (obrigatório)</li>
                <li><strong>email</strong> (opcional)</li>
                <li><strong>telefone</strong> (opcional)</li>
                <li>
                  <strong>área</strong> (obrigatório, deve existir em Parâmetros
                  Gerais > Setor)
                </li>
                <li><strong>cargo</strong> (opcional)</li>
                <li><strong>CPF/CNPJ</strong> (obrigatório)</li>
              </ul>
              Use separador ponto e vírgula (;) para melhor compatibilidade com
              Excel. A primeira linha deve conter os cabeçalhos.
            </div>
            <div v-else-if="importType === 'third'">
              <strong>Formato esperado (Terceiros):</strong>
              <ul class="mb-2">
                <li>
                  <strong>tipo</strong> (obrigatório: <em>individuo</em> ou
                  <em>entidade</em>)
                </li>
                <li>
                  <strong>nome</strong> (obrigatório: nome do indivíduo ou razão
                  social da entidade)
                </li>
                <li><strong>email</strong> (opcional)</li>
                <li><strong>telefone</strong> (opcional)</li>
                <li><strong>CPF/CNPJ</strong> (obrigatório)</li>
                <li>
                  <strong>nome do responsável</strong> (opcional, apenas para
                  entidades)
                </li>
                <li>
                  <strong>email do responsável</strong> (opcional, apenas para
                  entidades)
                </li>
              </ul>
              Use separador ponto e vírgula (;) para melhor compatibilidade com
              Excel. A primeira linha deve conter os cabeçalhos.
            </div>
            <div v-else>
              Selecione um tipo para ver os campos esperados do CSV.
            </div>
          </b-alert>

          <div class="d-flex align-items-center mb-3">
            <b-form-file
              v-model="importFile"
              accept=".csv,text/csv"
              placeholder="Escolha um arquivo CSV..."
              browse-text="Procurar"
              @change="onImportFileChange"
            />
            <b-button
              variant="link"
              class="ml-2 p-0"
              @click="downloadSampleCsv"
              :disabled="!importType"
            >
              Baixar modelo CSV
            </b-button>
          </div>

          <div v-if="importPreviewObjects.length" class="mb-2 text-muted">
            Pré-visualização: {{ importPreviewObjects.length }} registro(s)
          </div>

          <b-alert v-if="importErrors.length" variant="danger" show>
            <div class="mb-1"><strong>Erros encontrados:</strong></div>
            <ul class="mb-0">
              <li v-for="(err, idx) in importErrors" :key="idx">{{ err }}</li>
            </ul>
          </b-alert>

          <div v-if="importPreviewObjects.length" class="table-container mb-3">
            <b-table
              :items="importPreviewObjects"
              :fields="
                importType === 'internal'
                  ? previewFieldsInternal
                  : previewFieldsThird
              "
              small
              striped
              hover
              responsive
              head-variant="light"
              show-empty
              empty-text="Nenhum registro para exibir"
            />
          </div>

          <div class="d-flex justify-content-end mt-3">
            <b-button
              variant="secondary"
              class="mr-2"
              @click="isImportVisible = false"
              :disabled="!!importBusy"
              >Fechar</b-button
            >
            <b-button
              variant="primary"
              type="submit"
              :disabled="
                !importType ||
                !importFile ||
                importBusy ||
                !!importErrors.length
              "
            >
              <b-spinner small v-if="importBusy" class="mr-2" />
              Importar
            </b-button>
          </div>

          <div v-if="importBusy" class="text-muted mt-3">
            Importando... isso pode levar alguns segundos.
          </div>
        </b-form>
      </div>
    </b-sidebar>

    <!-- Sidebar para formulário -->
    <b-sidebar
      id="form-sidebar"
      v-model="isFormVisible"
      :title="getModalTitle()"
      right
      width="40%"
      shadow
      backdrop
      @hidden="closeForm"
    >
      <div class="form-sidebar p-3">
        <internal-employee-form
          v-if="isFormVisible && formType === 'internal'"
          :employee="selectedItem"
          :sector-options="sectors"
          @submit="handleFormSubmit"
          @delete="handleDelete"
          @close="closeForm"
        />
        <third-party-form
          v-if="isFormVisible && formType === 'third'"
          :third-party="selectedItem"
          @submit="handleFormSubmit"
          @delete="handleDelete"
          @close="closeForm"
        />
      </div>
    </b-sidebar>
  </b-row>
</template>

<script>
import { mapGetters } from "vuex";
import InternalEmployeeForm from "./InternalEmployeeForm.vue";
import ThirdPartyForm from "./ThirdPartyForm.vue";
import {
  getInternalEmployees,
  getThirdParties,
  createRelationship,
  updateRelationship,
  deleteRelationship,
} from "@/api/relationships";
import { getClassificationValues } from "@/api/company";

export default {
  name: "RelacionamentosView",
  components: {
    InternalEmployeeForm,
    ThirdPartyForm,
  },
  props: {
    id: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      internalEmployees: [],
      thirdParties: [],
      loadingInternal: false,
      loadingThird: false,
      isFormVisible: false,

      // Import CSV state
      isImportVisible: false,
      importType: null, // 'internal' | 'third'
      importFile: null,
      importBusy: false,
      importErrors: [],
      importPreviewObjects: [],
      previewFieldsInternal: [
        { key: "name", label: "Nome" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Telefone" },
        { key: "area", label: "Área" },
        { key: "position", label: "Cargo" },
        { key: "cpfcnpj", label: "CPF/CNPJ" },
        { key: "_status", label: "Status" },
      ],
      previewFieldsThird: [
        { key: "type", label: "Tipo" },
        { key: "name", label: "Nome/Razão Social" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Telefone" },
        { key: "cpfcnpj", label: "CPF/CNPJ" },
        { key: "responsibleName", label: "Nome do Responsável" },
        { key: "responsibleEmail", label: "Email do Responsável" },
        { key: "_status", label: "Status" },
      ],

      selectedItem: null,
      formType: null, // 'internal' ou 'third'
      sectors: [],
      loadingSectors: false,
      internalFields: [
        {
          key: "name",
          label: "NOME",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "email",
          label: "EMAIL",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "phone",
          label: "TELEFONE",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "area",
          label: "ÁREA",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "position",
          label: "CARGO",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "cpfcnpj",
          label: "CPF/CNPJ",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "active",
          label: "STATUS",
          sortable: true,
          tdClass: "align-middle",
        },
      ],
      thirdFields: [
        {
          key: "type",
          label: "TIPO",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "name",
          label: "NOME",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "email",
          label: "EMAIL",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "phone",
          label: "TELEFONE",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "additional",
          label: "INFORMAÇÕES ADICIONAIS",
          tdClass: "align-middle",
        },
        {
          key: "responsible",
          label: "RESPONSÁVEL",
          tdClass: "align-middle",
        },
        {
          key: "active",
          label: "STATUS",
          sortable: true,
          tdClass: "align-middle",
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["profile", "permissions"]),
    canManageRelationships() {
      const role = this.profile.role;
      if (role === "root" || role === "company-root") {
        return true;
      }
      return !!this.permissions?.company?.edit;
    },
    companyId() {
      // Se for admin (root), usa o ID da rota
      const companyId = parseInt(this.id || this.$route.params.id);
      if (this.profile.role === "root") {
        return companyId;
      }
      if (this.profile.managedCompanies?.includes(companyId)) {
        return companyId;
      }
      // Se for company-root, usa o ID da empresa do perfil
      return this.profile.company;
    },
    hasSectors() {
      return this.sectors.length > 0;
    },
  },
  async created() {
    await this.loadData();
  },
  watch: {
    // Recarregar dados quando mudar de empresa
    "$route.params.id": {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.loadData();
        }
      },
      immediate: false,
    },
    companyId(newId, oldId) {
      if (newId && newId !== oldId) {
        this.loadData();
      }
    },
    importType(newVal) {
      // Clear preview/errors when switching type
      this.importErrors = [];
      this.importPreviewObjects = [];
      if (this.importFile && newVal) {
        this.onImportFileChange();
      }
    },
    importFile(newVal) {
      // Generate preview as soon as a file is selected
      if (newVal && this.importType) {
        this.onImportFileChange();
      }
    },
  },
  methods: {
    async loadData() {
      await Promise.all([
        this.loadInternalEmployees(),
        this.loadThirdParties(),
        this.loadSectors(),
      ]);
    },
    async loadSectors() {
      this.loadingSectors = true;
      try {
        const response = await getClassificationValues(this.companyId, 7);
        this.sectors = (response.data || [])
          .map((item) => (item?.value || "").trim())
          .filter(Boolean);
      } catch (error) {
        this.sectors = [];
      } finally {
        this.loadingSectors = false;
      }
    },
    async loadInternalEmployees() {
      this.loadingInternal = true;
      try {
        const response = await getInternalEmployees(this.companyId);
        this.internalEmployees = response.data;
      } catch (error) {
        this.$bvToast.toast("Não foi possível carregar os funcionários.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        this.internalEmployees = [];
      } finally {
        this.loadingInternal = false;
      }
    },
    async loadThirdParties() {
      this.loadingThird = true;
      try {
        const response = await getThirdParties(this.companyId);
        this.thirdParties = response.data;
      } catch (error) {
        this.$bvToast.toast("Não foi possível carregar os terceiros.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        this.thirdParties = [];
      } finally {
        this.loadingThird = false;
      }
    },
    goBack() {
      this.$router.push({ path: "/empresas" });
    },
    openImportModal() {
      this.isImportVisible = true;
    },
    resetImport() {
      this.importType = null;
      this.importFile = null;
      this.importBusy = false;
      this.importErrors = [];
      this.importPreviewObjects = [];
    },
    async onImportFileChange() {
      this.importErrors = [];
      this.importPreviewObjects = [];
      if (!this.importFile) return;
      if (!this.importType) {
        this.importErrors = ["Selecione o tipo antes de escolher o arquivo."];
        return;
      }
      try {
        const text = await this.readFileAsText(this.importFile);
        const { headers, rows } = this.parseCsv(text);
        if (!headers.length) {
          this.importErrors = ["CSV sem cabeçalhos."];
          return;
        }
        const preview = [];
        const existingMap = this.buildExistingCpfMap(this.importType);
        const seenCsv = new Map();
        rows.forEach((row, idx) => {
          try {
            const obj = this.normalizeHeaders(this.rowToObject(headers, row));
            if (this.importType === "internal") {
              const item = {
                name: obj.name || "",
                email: obj.email || "",
                phone: obj.phone || "",
                area: obj.area || "",
                position: obj.position || "",
                cpfcnpj: obj.cpfcnpj || "",
                _status: "",
              };
              if (!item.name) throw new Error("Linha sem 'nome'.");
              if (!item.cpfcnpj) throw new Error("Linha sem 'CPF/CNPJ'.");
              if (!this.hasSectors) {
                throw new Error(
                  "Nenhum setor cadastrado em Parâmetros Gerais para importar funcionários."
                );
              }
              if (!item.area || !item.area.trim()) {
                throw new Error("Linha sem 'área'.");
              }
              if (!this.sectors.includes(item.area.trim())) {
                throw new Error(
                  `Setor inválido na coluna área: \"${item.area}\".`
                );
              }
              item.area = item.area.trim();
              const norm = this.normalizeCpfCnpj(item.cpfcnpj);
              if (existingMap.has(norm)) {
                const existing = existingMap.get(norm);
                const mismatch =
                  (existing.name || "").trim() !== item.name.trim();
                item._status = mismatch
                  ? `Já cadastrado (CPF igual). Nome diferente: "${
                      existing.name || ""
                    }"`
                  : "Já cadastrado (CPF igual)";
                item._skip = true;
              } else if (seenCsv.has(norm)) {
                const first = seenCsv.get(norm);
                item._status = `Duplicado no arquivo (primeira ocorrência na linha ${
                  first + 2
                })`;
                item._skip = true;
              } else {
                seenCsv.set(norm, idx);
              }
              preview.push(item);
            } else {
              const item = {
                type: obj.type || "",
                name: obj.name || "",
                email: obj.email || "",
                phone: obj.phone || "",
                cpfcnpj: obj.cpfcnpj || "",
                responsibleName: obj.responsibleName || "",
                responsibleEmail: obj.responsibleEmail || "",
                _status: "",
              };
              if (!item.type) throw new Error("Linha sem 'tipo'.");
              if (!item.name) throw new Error("Linha sem 'nome'.");
              if (!item.cpfcnpj) throw new Error("Linha sem 'CPF/CNPJ'.");
              const norm = this.normalizeCpfCnpj(item.cpfcnpj);
              if (existingMap.has(norm)) {
                const existing = existingMap.get(norm);
                const existingName =
                  existing.type === "individuo"
                    ? existing.personName || existing.name || ""
                    : existing.entityName || existing.name || "";
                const mismatch = existingName.trim() !== item.name.trim();
                item._status = mismatch
                  ? `Já cadastrado (CPF/CNPJ igual). Nome diferente: "${existingName}"`
                  : "Já cadastrado (CPF/CNPJ igual)";
                item._skip = true;
              } else if (seenCsv.has(norm)) {
                const first = seenCsv.get(norm);
                item._status = `Duplicado no arquivo (primeira ocorrência na linha ${
                  first + 2
                })`;
                item._skip = true;
              } else {
                seenCsv.set(norm, idx);
              }
              preview.push(item);
            }
          } catch (e) {
            this.importErrors.push(`Linha ${idx + 2}: ${e.message || e}`);
          }
        });
        this.importPreviewObjects = preview;
      } catch (e) {
        this.importErrors = [e.message || "Erro ao ler arquivo"];
      }
    },
    async handleImportSubmit() {
      this.importErrors = [];
      if (!this.importType) {
        this.importErrors.push(
          "Selecione o tipo de relacionamento a importar."
        );
        return;
      }
      if (!this.importFile) {
        this.importErrors.push("Selecione um arquivo CSV.");
        return;
      }

      this.importBusy = true;
      try {
        const text = await this.readFileAsText(this.importFile);
        const { headers, rows } = this.parseCsv(text);
        if (!headers.length) {
          this.importErrors.push("CSV sem cabeçalhos.");
          this.importBusy = false;
          return;
        }

        const mapped = [];
        const errors = [];
        const existingMap = this.buildExistingCpfMap(this.importType);
        const seenCsv = new Set();
        let skippedExisting = 0;
        let skippedInFile = 0;
        rows.forEach((row, idx) => {
          try {
            const obj = this.rowToObject(headers, row);
            const payload = this.mapToRelationshipPayload(obj, this.importType);
            const norm = this.normalizeCpfCnpj(payload.cpfcnpj);
            if (existingMap.has(norm)) {
              skippedExisting++;
              return; // skip existing
            }
            if (seenCsv.has(norm)) {
              skippedInFile++;
              return; // skip duplicates in same CSV
            }
            seenCsv.add(norm);
            mapped.push(payload);
          } catch (e) {
            errors.push(`Linha ${idx + 2}: ${e.message || e}`);
          }
        });

        if (errors.length) {
          this.importErrors = errors;
          this.importBusy = false;
          return;
        }

        let ok = 0;
        let fail = 0;
        for (let i = 0; i < mapped.length; i++) {
          try {
            await createRelationship(mapped[i]);
            ok++;
          } catch (e) {
            fail++;
            if (this.importErrors.length < 10) {
              this.importErrors.push(
                `Falha ao importar linha ${i + 2}: ${
                  e?.response?.data?.message || e.message || "Erro desconhecido"
                }`
              );
            }
          }
        }

        if (fail === 0) {
          this.$bvToast.toast(
            `${ok} importado(s). ${
              skippedExisting
                ? skippedExisting + " já existente(s) ignorado(s). "
                : ""
            }${
              skippedInFile
                ? skippedInFile + " duplicado(s) no arquivo ignorado(s)."
                : ""
            }`,
            {
              title: "Sucesso",
              toastClass: "custom-toast custom-toast-success",
              variant: "light",
              solid: true,
              autoHideDelay: 5000,
              noCloseButton: false,
            }
          );
          this.isImportVisible = false;
          await this.loadData();
        } else {
          this.$bvToast.toast(
            `${ok} sucesso(s), ${fail} falha(s). ${
              skippedExisting ? skippedExisting + " já existente(s). " : ""
            }${
              skippedInFile ? skippedInFile + " duplicado(s) no arquivo." : ""
            } Revise os erros abaixo.`,
            {
              title: "Atenção",
              toastClass: "custom-toast custom-toast-warning",
              variant: "light",
              solid: true,
              autoHideDelay: 8000,
              noCloseButton: false,
            }
          );
        }
      } catch (err) {
        this.importErrors = [err.message || "Erro ao processar o arquivo CSV."];
      } finally {
        this.importBusy = false;
      }
    },
    normalizeCpfCnpj(v) {
      if (!v) return "";
      return String(v).replace(/\D/g, "");
    },
    buildExistingCpfMap(type) {
      const map = new Map();
      if (type === "internal") {
        for (const it of this.internalEmployees || []) {
          const norm = this.normalizeCpfCnpj(it.cpfcnpj);
          if (norm) map.set(norm, it);
        }
      } else {
        for (const it of this.thirdParties || []) {
          const norm = this.normalizeCpfCnpj(it.cpfcnpj);
          if (norm) map.set(norm, it);
        }
      }
      return map;
    },
    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () =>
          reject(reader.error || new Error("Falha ao ler arquivo"));
        reader.readAsText(file, "utf-8");
      });
    },
    detectDelimiter(sampleLine) {
      const sc = (sampleLine.match(/;/g) || []).length;
      const cc = (sampleLine.match(/,/g) || []).length;
      return sc >= cc ? ";" : ",";
    },
    parseCsv(text) {
      const lines = text
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n")
        .filter((l) => l.trim().length > 0);
      if (!lines.length) return { headers: [], rows: [] };
      const delimiter = this.detectDelimiter(lines[0]);
      const parseLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
              current += '"';
              i++;
            } else {
              inQuotes = !inQuotes;
            }
          } else if (ch === delimiter && !inQuotes) {
            result.push(current);
            current = "";
          } else {
            current += ch;
          }
        }
        result.push(current);
        return result.map((v) => v.trim());
      };
      const headers = parseLine(lines[0]).map((h) =>
        h.replace(/^\ufeff/, "").trim()
      );
      const rows = lines.slice(1).map(parseLine);
      return { headers, rows };
    },
    rowToObject(headers, row) {
      const obj = {};
      headers.forEach((h, i) => {
        obj[h] = row[i] !== undefined ? row[i] : "";
      });
      return obj;
    },
    normalizeHeaders(obj) {
      // Aceitar cabeçalhos em pt-br comuns
      const normalized = { ...obj };
      const map = [
        { from: "tipo", to: "type" },
        { from: "nome", to: "name" },
        { from: "telefone", to: "phone" },
        { from: "cpf", to: "cpfcnpj" },
        { from: "cnpj", to: "cpfcnpj" },
        { from: "cpf/cnpj", to: "cpfcnpj" },
        { from: "cpf_cnpj", to: "cpfcnpj" },
        { from: "área", to: "area" },
        { from: "area", to: "area" },
        { from: "cargo", to: "position" },
        { from: "nome do responsável", to: "responsibleName" },
        { from: "responsavel", to: "responsibleName" },
        { from: "email do responsável", to: "responsibleEmail" },
      ];
      for (const { from, to } of map) {
        const key = Object.keys(normalized).find(
          (k) => k.toLowerCase().trim() === from
        );
        if (key && !(to in normalized)) normalized[to] = normalized[key];
      }
      return normalized;
    },
    mapToRelationshipPayload(obj, type) {
      if (type === "internal") {
        obj = this.normalizeHeaders(obj);
        if (!obj.name || !obj.name.trim()) {
          throw new Error("Campo obrigatório ausente: nome");
        }
        if (!obj.cpfcnpj || !obj.cpfcnpj.trim()) {
          throw new Error("Campo obrigatório ausente: CPF/CNPJ");
        }
        if (!this.hasSectors) {
          throw new Error(
            "Nenhum setor cadastrado em Parâmetros Gerais. Cadastre setores para continuar."
          );
        }
        const area = obj.area?.trim();
        if (!area) {
          throw new Error("Campo obrigatório ausente: área");
        }
        if (!this.sectors.includes(area)) {
          throw new Error(
            `Setor inválido no campo área: \"${area}\". Use um setor de Parâmetros Gerais.`
          );
        }
        return {
          relationshipType: "internal",
          name: obj.name?.trim(),
          email: obj.email?.trim() || undefined,
          phone: obj.phone?.trim() || undefined,
          area,
          position: obj.position?.trim() || undefined,
          cpfcnpj: obj.cpfcnpj?.trim() || undefined,
          active: true,
          company: this.companyId,
        };
      } else {
        obj = this.normalizeHeaders(obj);
        const tp = (obj.type || "").toLowerCase().trim();
        if (!tp || (tp !== "individuo" && tp !== "entidade")) {
          throw new Error(
            "Campo obrigatório inválido: tipo (use 'individuo' ou 'entidade')"
          );
        }
        if (!obj.name || !obj.name.trim()) {
          throw new Error("Campo obrigatório ausente: nome");
        }
        if (!obj.cpfcnpj || !obj.cpfcnpj.trim()) {
          throw new Error("Campo obrigatório ausente: CPF/CNPJ");
        }
        const base = {
          relationshipType: "third-party",
          type: tp,
          name: obj.name?.trim(),
          cpfcnpj: obj.cpfcnpj?.trim() || undefined,
          active: true,
          company: this.companyId,
        };
        if (tp === "individuo") {
          return {
            ...base,
            personEmail: obj.email?.trim() || undefined,
            personPhone: obj.phone?.trim() || undefined,
            personName: obj.name?.trim(),
          };
        } else {
          return {
            ...base,
            entityEmail: obj.email?.trim() || undefined,
            entityPhone: obj.phone?.trim() || undefined,
            responsibleName: obj.responsibleName?.trim() || undefined,
            responsibleEmail: obj.responsibleEmail?.trim() || undefined,
            entityName: obj.name?.trim(),
          };
        }
      }
    },
    downloadSampleCsv() {
      if (!this.importType) return;
      const sep = ";";
      const bom = "\ufeff";
      let headers = [];
      let rows = [];
      if (this.importType === "internal") {
        headers = ["nome", "email", "telefone", "área", "cargo", "CPF/CNPJ"];
        rows = [
          [
            "Maria da Silva",
            "maria@empresa.com",
            "(11) 99999-9999",
            "Financeiro",
            "Analista",
            "123.456.789-00",
          ],
        ];
      } else {
        headers = [
          "tipo",
          "nome",
          "email",
          "telefone",
          "CPF/CNPJ",
          "nome do responsável",
          "email do responsável",
        ];
        rows = [
          [
            "individuo",
            "João Souza",
            "joao@email.com",
            "(11) 99999-1111",
            "123.456.789-00",
            "",
            "",
          ],
          [
            "entidade",
            "ACME LTDA",
            "contato@acme.com",
            "(11) 2222-3333",
            "12.345.678/0001-90",
            "Maria Gestora",
            "gestora@acme.com",
          ],
        ];
      }
      const content = [headers, ...rows]
        .map((r) =>
          r
            .map((v) => {
              const s = String(v == null ? "" : v);
              const needsQ = /[";\n\r]/.test(s);
              const esc = s.replace(/"/g, '""');
              return needsQ ? `"${esc}"` : esc;
            })
            .join(sep)
        )
        .join("\r\n");
      const blob = new Blob([bom + content], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        this.importType === "internal"
          ? "modelo_funcionarios.csv"
          : "modelo_terceiros.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    openCreateForm(type) {
      if (type === "internal" && !this.hasSectors) {
        this.$bvToast.toast(
          "Cadastre setores em Parâmetros Gerais para cadastrar funcionários internos.",
          {
            title: "Atenção",
            toastClass: "custom-toast custom-toast-warning",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
        return;
      }

      this.formType = type;
      this.selectedItem = null;
      this.isFormVisible = true;
    },
    openEditForm(item) {
      // Determinar o tipo baseado no relationshipType
      if (item.relationshipType === "third-party") {
        this.formType = "third";
      } else {
        this.formType = "internal";
      }
      this.selectedItem = item;
      this.isFormVisible = true;
    },
    closeForm() {
      this.isFormVisible = false;
      this.selectedItem = null;
      this.formType = null;
    },
    getModalTitle() {
      if (!this.formType) return "";
      const isEditing = !!this.selectedItem;
      const typeText =
        this.formType === "internal" ? "Funcionário" : "Terceiro";
      return isEditing ? `Editar ${typeText}` : `Novo ${typeText}`;
    },
    async handleFormSubmit(formData) {
      try {
        if (this.formType === "internal" && !this.hasSectors) {
          throw new Error(
            "Cadastre setores em Parâmetros Gerais para cadastrar funcionários internos."
          );
        }

        const relationshipData = {
          ...formData,
          relationshipType:
            this.formType === "internal" ? "internal" : "third-party",
          company: this.companyId,
        };
        if (formData.id) {
          await updateRelationship(formData.id, relationshipData);
        } else {
          await createRelationship(relationshipData);
        }
        this.$bvToast.toast(
          `${this.formType === "internal" ? "Funcionário" : "Terceiro"} ${
            formData.id ? "atualizado" : "criado"
          } com sucesso.`,
          {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );
        this.closeForm();
        await this.loadData();
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          `Não foi possível ${formData.id ? "atualizar" : "criar"} o ${
            this.formType === "internal" ? "funcionário" : "terceiro"
          }.`;

        this.$bvToast.toast(errorMessage, {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },
    async handleDelete(item) {
      try {
        await deleteRelationship(item._id);
        this.$bvToast.toast(
          `${
            this.formType === "internal" ? "Funcionário" : "Terceiro"
          } excluído com sucesso.`,
          {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );
        this.closeForm();
        await this.loadData();
      } catch (error) {
        this.$bvToast.toast(
          `Não foi possível excluir o ${
            this.formType === "internal" ? "funcionário" : "terceiro"
          }.`,
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      }
    },
    rowClass(item, type) {
      if (!item || type !== "row") return "employee-row";
      return ["employee-row"];
    },
  },
};
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.table-container >>> .employees-header th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
  border: 0;
  white-space: nowrap;
}

.table-container >>> .employees-header th.sortable:hover {
  color: #0056b3;
}

.table-container >>> .employee-row {
  cursor: pointer;
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease-in-out;
}

.table-container >>> .employee-row:hover {
  background-color: #e9ecef !important;
}

.table-container >>> tbody tr:last-of-type {
  border-bottom: none;
}

.table-container >>> .employee-row td {
  border-top: none;
  vertical-align: middle;
  padding: 0.85rem 0.75rem;
}

.employee-name,
.third-party-name {
  font-weight: 500;
  color: #495057;
}

.employee-email,
.third-party-email {
  font-size: 0.9rem;
  color: #6c757d;
}

.employee-phone,
.third-party-phone {
  font-size: 0.9rem;
  color: #6c757d;
}

.employee-cpfcnpj {
  font-size: 0.9rem;
  color: #6c757d;
}

.employee-area,
.employee-position {
  font-size: 0.9rem;
  color: #6c757d;
}

.employee-status,
.third-party-status,
.third-party-type {
  text-align: center;
}

.third-party-additional,
.third-party-responsible {
  font-size: 0.85rem;
}

/* Import sidebar spacing */
.import-sidebar {
  padding: 1rem !important;
}
.import-sidebar .b-form-group {
  margin-bottom: 0.75rem;
}
.import-sidebar .alert {
  margin-bottom: 1rem;
}
.import-sidebar .table-container {
  margin-top: 0.5rem;
}

/* Form sidebar spacing */
.form-sidebar {
  padding: 1rem !important;
}
.form-sidebar .b-form-group {
  margin-bottom: 0.75rem;
}
</style>
